// src/pages/Bistro/Bistro.tsx
import React, { useState, useEffect, Fragment } from "react";
import parse from "html-react-parser";
import Slider, { Settings } from "react-slick";
import { hostname } from "../../constants";
import Page from "../../layouts/Page";
import TextBlock from "../../components/TextBlock";
import Button from "../../components/Button";
import MiniAddress from "../../components/MiniAddress";
import styles from "./styles.module.sass";

interface SlideImage {
  id: number;
  url: string;
}

interface ACFLocation {
  address: string;
  hours: string;
  email: string;
  facebook?: string;
  instagram?: string;
  tripadvisor?: string;
}

interface ACF {
  intro?: string;
  slide?: { image: SlideImage }[];
  location?: ACFLocation[];
}

interface PageData {
  id: number;
  title: { rendered: string };
  content: { rendered: string };
  acf: ACF;
}

const sliderSettings: Settings = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  responsive: [
    { breakpoint: 600, settings: { slidesToScroll: 1, initialSlide: 1 } },
  ],
};

const info = {
  address: `Lloyd's Inn Bali, GF
        <br />
        Jln Arjuna (Double Six),
        <br />
        Seminyak, Kuta, Kabupaten Badung,
        <br />
        Bali 80361`,
  hours: "9am - 9pm",
  email: "opeum@lloydsinn.com",
  fb: "https://www.facebook.com/opeumbistro",
  ig: "https://www.instagram.com/opeumbistro",
  ta: "https://www.tripadvisor.com.au/Restaurant_Review-g469404-d15327227-Reviews-Opeum_Bistro-Seminyak_Kuta_District_Bali.html",
  tel: "+62 361 934 8444",
};

const links: Record<number, string> = {
  73006: "https://www.facebook.com/opeumbistro/menu/",
  73387: "https://docdro.id/UnW1cKQ",
};

const Bistro: React.FC = () => {
  const [dataGroup, setDataGroup] = useState<PageData[]>([]);

  useEffect(() => {
    Promise.all([
      fetch(`${hostname}/wp-json/wp/v2/pages/73006`).then((res) => res.json()),
      fetch(`${hostname}/wp-json/wp/v2/pages/73387`).then((res) => res.json()),
    ])
      .then(([data1, data2]) => setDataGroup([data1, data2]))
      .catch(console.error);
  }, []);

  return (
    <Page className={styles.base}>
      {dataGroup.map(
        (group) =>
          group.content && (
            <Fragment key={group.id}>
              {group.title.rendered && (
                <TextBlock className={styles.title}>
                  <h1>{group.title.rendered}</h1>
                </TextBlock>
              )}
              <TextBlock className={styles.intro} width={800}>
                {group.acf?.intro && parse(group.acf.intro)}
                {group.acf?.slide && (
                  <Slider className={styles.slider} {...sliderSettings}>
                    {group.acf.slide.map((slide) => (
                      <div key={`slide-${slide.image.id}`}>
                        <img src={slide.image.url} alt="" />
                      </div>
                    ))}
                  </Slider>
                )}
                {parse(group.content.rendered)}
              </TextBlock>
              <TextBlock width={725}>
                <div className={styles.actions}>
                  {links[group.id] && (
                    <Button className={styles.btn} href={links[group.id]}>
                      View menu
                    </Button>
                  )}
                  {group.id === 73006 && (
                    <Button
                      className={styles.btn}
                      href="https://bookv5.chope.co/booking?rid=opeumbistro1908bal&source=rest_lloydsinn.com"
                    >
                      Book a table
                    </Button>
                  )}
                </div>
              </TextBlock>
            </Fragment>
          )
      )}

      {dataGroup.map(
        (group) =>
          group.acf.location &&
          group.acf.location[0] && (
            <MiniAddress
              key={group.id}
              title={group.title.rendered}
              address={group.acf.location[0].address}
              hours={group.acf.location[0].hours}
              email={group.acf.location[0].email}
              fb={group.acf.location[0].facebook || ""}
              ig={group.acf.location[0].instagram || ""}
              ta={group.acf.location[0].tripadvisor || ""}
            />
          )
      )}
    </Page>
  );
};

export default Bistro;
